import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Button } from 'react-bootstrap';
import PatientReplacementRow from './PatientReplacementRow';
import NoCandidatesAlert from './NoCandidatesAlert';

const FacilityDependencySection = ({
  facility,
  selections,
  onSelectionChange,
  errors = []
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const renderRows = () => {
    const rows = [];

    facility.patients.forEach(patient => {
      if (['nurse', 'both'].includes(patient.dependency_type)) {
        const error = errors.find(
          e => e.patient_id === patient.patient_id && e.field === 'nurse'
        )?.message;

        rows.push(
          <PatientReplacementRow
            key={`${patient.patient_id}-nurse`}
            patient={{ ...patient, dependency_type: 'nurse' }}
            selectedValue={selections.nurse?.[patient.patient_id]}
            onChange={val =>
              onSelectionChange('nurse', patient.patient_id, val)
            }
            error={error}
          />
        );
      }

      if (['case_manager', 'both'].includes(patient.dependency_type)) {
        const error = errors.find(
          e =>
            e.patient_id === patient.patient_id && e.field === 'case_manager'
        )?.message;

        rows.push(
          <PatientReplacementRow
            key={`${patient.patient_id}-case-manager`}
            patient={{ ...patient, dependency_type: 'case_manager' }}
            selectedValue={selections.case_manager?.[patient.patient_id]}
            onChange={val =>
              onSelectionChange('case_manager', patient.patient_id, val)
            }
            error={error}
          />
        );
      }
    });

    return rows;
  };

  const hasNoCandidates = facility.patients.some(
    patient =>
      !patient.replacement_candidates ||
      patient.replacement_candidates.length === 0
  );
  const patientCount = facility.patients.length;

  return (
    <section className="bg-card px-3 py-3 mb-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <h6 className="mb-0 fw-semibold text-800 fs-9">
            {facility.facility_name}
          </h6>
          <Badge bg="light" text="secondary" className="border fw-semibold">
            {patientCount} patient{patientCount === 1 ? '' : 's'}
          </Badge>
        </div>
        {facility.patients.length > 5 && (
          <Button
            variant="link"
            size="sm"
            className="p-0 fs-10"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        )}
      </div>

      {hasNoCandidates && <NoCandidatesAlert />}

      {(isExpanded || facility.patients.length <= 5) && (
        <div className="d-flex flex-column gap-2">{renderRows()}</div>
      )}
    </section>
  );
};

FacilityDependencySection.propTypes = {
  facility: PropTypes.shape({
    facility_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    facility_name: PropTypes.string,
    patients: PropTypes.arrayOf(
      PropTypes.shape({
        patient_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        dependency_type: PropTypes.string,
        replacement_candidates: PropTypes.array
      })
    )
  }).isRequired,
  selections: PropTypes.shape({
    nurse: PropTypes.object,
    case_manager: PropTypes.object
  }).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  errors: PropTypes.array
};

export default FacilityDependencySection;
